import React, { useState, useEffect } from "react";
import ModalComponent from "../../components/modals";
import { Grid } from "@mui/material";
import SimpleSelect from "../../components/selects/simpleSelect";
import FormTextField from "../../components/textField/formTextField";
import LaddaButton from "../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { icons } from "../../constants";
import SimpleTable from "../../components/tables/simpleTable";
import FormMultiSelect from "../../components/selects/formMultiSelect";
import { StyledTooltip } from "../../components/toolltipComponent";
import { getCausesTooltipContent } from "./foreignTradeUtils";
import {
  callApiAsync,
  getSuppliersForeignTrade,
  getImportExecutionCauses,
  getImportExecutionsByMonth,
  createOrUpdateImportExecution,
  deleteImportExecution
} from "../../services/apiService";

const columns = [
  { field: "product", header: "Producto", align: "center", sortable: true },
  { field: "supplier_name", header: "Marca", align: "center" },
  { field: "order_number", header: "Número de Orden", align: "center" },
  { field: "causes_text", header: "Causas", align: "center" },
  { field: "actions", header: "", columnType: "actionButtons" }
];

const AddExecutionModal = ({
  open = false,
  handleClose = () => { },
  data = null,
  year = null,
  onSaved = () => { }
}) => {
  const [executionsList, setExecutionsList] = useState([]);
  const [suppliersOptions, setSuppliersOptions] = useState([]);
  const [causesOptions, setCausesOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const validationSchema = yup.object().shape({
    supplier_id: yup.mixed().required("El proveedor es obligatorio"),
    product: yup.string().trim().required("El producto/marca es obligatorio"),
    order_number: yup.string().trim().required("El número de orden es obligatorio"),
    cause_ids: yup.array()
  });

  const {
    control: controlForm,
    handleSubmit,
    getValues,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      supplier_id: "",
      product: "",
      order_number: "",
      cause_ids: []
    }
  });

  const monthNum = data?.month != null ? Number(data.month) : null;
  const yearNum = year != null ? Number(year) : null;
  const canLoad = open && monthNum >= 1 && monthNum <= 12 && !Number.isNaN(yearNum);

  const loadExecutions = () => {
    if (!canLoad) return;
    callApiAsync(
      () => getImportExecutionsByMonth(yearNum, monthNum),
      (list) => setExecutionsList(Array.isArray(list) ? list : [])
    );
  };

  useEffect(() => {
    if (!open) return;
    callApiAsync(() => getSuppliersForeignTrade(), (d) => {
      const arr = Array.isArray(d) ? d : [];
      setSuppliersOptions([{ name: "Seleccionar Proveedor", value: "" }, ...arr]);
    });
    callApiAsync(() => getImportExecutionCauses(), (d) => {
      const arr = Array.isArray(d) ? d : [];
      setCausesOptions(arr.map((c) => ({ name: `${c.code} - ${c.name}`, value: c.id })));
    });
  }, [open]);

  useEffect(() => {
    if (canLoad) loadExecutions();
  }, [open, yearNum, monthNum]);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      reset({ supplier_id: "", product: "", order_number: "", cause_ids: [] });
    }
  }, [open, reset]);

  const onSubmit = () => {
    const v = getValues();
    const supplierId = v.supplier_id?.value ?? v.supplier_id;
    const payload = {
      year: yearNum,
      month: monthNum,
      supplier_id: supplierId,
      product: v.product,
      order_number: v.order_number,
      cause_ids: Array.isArray(v.cause_ids) ? v.cause_ids : []
    };
    if (editingId) payload.id = editingId;
    callApiAsync(
      () => createOrUpdateImportExecution(payload),
      () => {
        setEditingId(null);
        reset({ supplier_id: "", product: "", order_number: "", cause_ids: [] });
        loadExecutions();
        onSaved();
      }
    );
  };

  const handleEdit = (rowData) => {
    setEditingId(rowData.id);
    reset({
      supplier_id: rowData.supplier_id,
      product: rowData.product || "",
      order_number: rowData.order_number || "",
      cause_ids: rowData.cause_ids || []
    });
  };

  const handleDelete = (rowData) => {
    if (!window.confirm("¿Eliminar esta ejecución?")) return;
    callApiAsync(
      () => deleteImportExecution(rowData.id),
      () => {
        loadExecutions();
        onSaved();
      }
    );
  };

  const title =
    data?.monthName && yearNum
      ? `Ejecución Real ${data.monthName} ${yearNum}`
      : "Ejecución Real";

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      title={title}
      actions={
        <LaddaButton variant="contained" onClick={handleClose}>
          <p className="button_text">Guardar</p>
        </LaddaButton>
      }
    >
      <div className="add-execution-modal-form">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4}>
              <p className="create-label ml-0">Proveedor:</p>
              <SimpleSelect
                name="supplier_id"
                control={controlForm}
                options={suppliersOptions}
                margin="0"
                error={errors.supplier_id}
                disabled={!canLoad}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <p className="create-label ml-0">Producto:</p>
              <FormTextField
                name="product"
                control={controlForm}
                margin="0"
                placeholder="Producto/marca"
                error={errors.product}
                disabled={!canLoad}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <p className="create-label ml-0">Nro. de Orden:</p>
              <FormTextField
                name="order_number"
                control={controlForm}
                margin="0"
                placeholder="Número de orden"
                error={errors.order_number}
                disabled={!canLoad}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <StyledTooltip wide title={getCausesTooltipContent()} placement="top">
                <div className="HContainer" style={{ alignItems: "center", marginBottom: 6, cursor: "pointer" }}>
                  <p className="create-label ml-0" style={{ marginBottom: 0 }}>Causas:</p>
                  <img
                    src={icons.infoExclamation}
                    alt="info causas"
                    className="btn-icon-table"
                    style={{ marginLeft: 6, marginTop: 6, width: 22, height: 22 }}
                  />
                </div>
              </StyledTooltip>
              <FormMultiSelect
                name="cause_ids"
                control={controlForm}
                label=""
                options={causesOptions}
                error={errors.cause_ids}
                disabled={!canLoad}
                margin="0"
              />
            </Grid>

          </Grid>
          <div className="j-center mt-20">
            <LaddaButton
              type="submit"
              variant="contained"
              height="40px"
              width="250px"
              margin="0"
              disabled={!canLoad}
            >
              <img className="btn-icon" src={icons.plusIcon} alt="+" style={{ marginRight: "8px" }} />
              <p className="button_text">{editingId ? "Actualizar" : "Agregar Ejecución"}</p>
            </LaddaButton>
          </div>
        </form>
      </div>
      <div style={{ marginTop: 24 }}>
        <SimpleTable
          data={executionsList}
          tableStyle={{ minWidth: "600px" }}
          columns={columns}
          buttons={[
            {
              type: "icon",
              icon: icons.editBlue,
              onClick: handleEdit
            },
            {
              type: "icon",
              icon: icons.redTrash || icons.trash,
              onClick: handleDelete
            }
          ]}
        />
      </div>
    </ModalComponent>
  );
};

export default AddExecutionModal;
